import { createCompany, deleteCompanyCascade } from "@repo/ui";
import { getFirestore } from "firebase/firestore";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { Company } from "../types";
import { CUSTOMER_TYPE_LABELS, CustomerTypeSelect } from "./CustomerTypeSelect";
import { TypedDeleteConfirmationDialog } from "./TypedDeleteConfirmationDialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";

type FeatureOverride = "inherit" | "enabled" | "disabled";

interface CompaniesPageProps {
	onNavigateToCompany: (companyId: string, edit?: boolean) => void;
}

export function CompaniesPage({ onNavigateToCompany }: CompaniesPageProps) {
	const { companies } = useData();
	const db = getFirestore();
	const [searchTerm, setSearchTerm] = useState("");
	const [customerTypeFilter, setCustomerTypeFilter] = useState("all");
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
	const [formData, setFormData] = useState({
		name: "",
		contactName: "",
		contactEmail: "",
		contactPhone: "",
		customerType: null as Company["customerType"],
		callerMemoryUserUi: "inherit" as FeatureOverride,
		callerMemoryAgentInjection: "inherit" as FeatureOverride,
		outboundPlaybookCalls: "inherit" as FeatureOverride,
		apiAccess: false,
		piiRemoval: "inherit" as FeatureOverride,
		deleteAudioFromTwilio: "inherit" as FeatureOverride,
		deletePiiJsonFromGcp: "inherit" as FeatureOverride,
	});

	const resetFormData = () =>
		setFormData({
			name: "",
			contactName: "",
			contactEmail: "",
			contactPhone: "",
			customerType: null,
			callerMemoryUserUi: "inherit",
			callerMemoryAgentInjection: "inherit",
			outboundPlaybookCalls: "inherit",
			apiAccess: false,
			piiRemoval: "inherit",
			deleteAudioFromTwilio: "inherit",
			deletePiiJsonFromGcp: "inherit",
		});

	const overrideToBoolean = (value: FeatureOverride) =>
		value === "inherit" ? undefined : value === "enabled";

	const buildCompanyPayload = () => {
		const features: Company["features"] = {};
		const callerMemoryUserUi = overrideToBoolean(formData.callerMemoryUserUi);
		const callerMemoryAgentInjection = overrideToBoolean(
			formData.callerMemoryAgentInjection,
		);
		const outboundPlaybookCalls = overrideToBoolean(
			formData.outboundPlaybookCalls,
		);
		const piiRemoval = overrideToBoolean(formData.piiRemoval);
		const deleteAudioFromTwilio = overrideToBoolean(
			formData.deleteAudioFromTwilio,
		);
		const deletePiiJsonFromGcp = overrideToBoolean(
			formData.deletePiiJsonFromGcp,
		);

		if (callerMemoryUserUi !== undefined) {
			features.callerMemoryUserUi = callerMemoryUserUi;
		}

		if (callerMemoryAgentInjection !== undefined) {
			features.callerMemoryAgentInjection = callerMemoryAgentInjection;
		}

		if (outboundPlaybookCalls !== undefined) {
			features.outboundPlaybookCalls = outboundPlaybookCalls;
		}
		features.apiAccess = formData.apiAccess;

		if (piiRemoval !== undefined) {
			features.piiRemoval = piiRemoval;
		}

		if (deleteAudioFromTwilio !== undefined) {
			features.deleteAudioFromTwilio = deleteAudioFromTwilio;
		}

		if (deletePiiJsonFromGcp !== undefined) {
			features.deletePiiJsonFromGcp = deletePiiJsonFromGcp;
		}

		return {
			name: formData.name,
			customerType: formData.customerType,
			contactName: formData.contactName,
			contactEmail: formData.contactEmail,
			contactPhone: formData.contactPhone,
			features,
		};
	};

	const filteredCompanies = useMemo(() => {
		const matchingCompanies = companies.filter(
			(company) =>
				customerTypeFilter === "all" ||
				(company.customerType ?? "unclassified") === customerTypeFilter,
		);
		if (!searchTerm) return matchingCompanies;

		return matchingCompanies.filter(
			(company) =>
				company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				company.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
				company.contactEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
				company.contactPhone.includes(searchTerm),
		);
	}, [companies, searchTerm, customerTypeFilter]);

	const formatDate = (dateString: Date | string | null | undefined) => {
		return new Date(dateString ?? "").toLocaleDateString();
	};

	const handleSubmit = async () => {
		// Validate form
		if (
			!formData.name ||
			!formData.contactName ||
			!formData.contactEmail ||
			!formData.contactPhone
		) {
			toast.error("Please fill in all fields");
			return;
		}

		try {
			await createCompany(db, buildCompanyPayload());
			toast.success(`Company "${formData.name}" created successfully`);

			// Reset form and close dialog
			resetFormData();
			setIsDialogOpen(false);
		} catch (error) {
			console.error("Failed to create company", error);
			toast.error("Failed to create company");
		}
	};

	const handleDeleteClick = (company: Company) => {
		setSelectedCompany(company);
		setIsDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedCompany || !selectedCompany.id) return;

		try {
			await deleteCompanyCascade({ companyId: selectedCompany.id });
			toast.success(`Company "${selectedCompany.name}" deleted successfully`);
			setIsDeleteDialogOpen(false);
			setSelectedCompany(null);
		} catch (error) {
			console.error("Failed to delete company", error);
			toast.error("Failed to delete company");
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-start">
				<div>
					<h1>Companies</h1>
					<p className="text-slate-600">
						Manage customer companies and their accounts
					</p>
				</div>
				<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
					<DialogTrigger asChild>
						<Button>
							<Plus className="mr-2 h-4 w-4" />
							Add Company
						</Button>
					</DialogTrigger>
					<DialogContent className="admin-editor-dialog">
						<DialogHeader>
							<DialogTitle>Add New Company</DialogTitle>
							<DialogDescription>
								Set up the company profile and its platform settings.
							</DialogDescription>
						</DialogHeader>
						<div className="admin-editor-body admin-editor-grid">
							<div className="admin-editor-section-heading">
								Company and contact
							</div>
							<div className="space-y-2">
								<Label htmlFor="company-name">Company Name</Label>
								<Input
									id="company-name"
									placeholder="Acme Corporation"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="contact-name">Contact Person</Label>
								<Input
									id="contact-name"
									placeholder="John Smith"
									value={formData.contactName}
									onChange={(e) =>
										setFormData({ ...formData, contactName: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="contact-email">Contact Email</Label>
								<Input
									id="contact-email"
									type="email"
									placeholder="john.smith@acme.com"
									value={formData.contactEmail}
									onChange={(e) =>
										setFormData({ ...formData, contactEmail: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="contact-phone">Contact Phone</Label>
								<Input
									id="contact-phone"
									placeholder="+1 (555) 123-4567"
									value={formData.contactPhone}
									onChange={(e) =>
										setFormData({ ...formData, contactPhone: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="new-company-customer-type">Customer type</Label>
								<CustomerTypeSelect
									id="new-company-customer-type"
									value={formData.customerType}
									onChange={(customerType) =>
										setFormData({ ...formData, customerType })
									}
								/>
							</div>
							<div className="admin-editor-section-heading">
								Capabilities and retention
							</div>
							<div className="space-y-2">
								<Label>Caller Memory: User UI</Label>
								<Select
									value={formData.callerMemoryUserUi}
									onValueChange={(value: FeatureOverride) =>
										setFormData({
											...formData,
											callerMemoryUserUi: value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="inherit">Use system default</SelectItem>
										<SelectItem value="enabled">Enabled for company</SelectItem>
										<SelectItem value="disabled">
											Disabled for company
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Caller Memory: Agent Injection</Label>
								<Select
									value={formData.callerMemoryAgentInjection}
									onValueChange={(value: FeatureOverride) =>
										setFormData({
											...formData,
											callerMemoryAgentInjection: value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="inherit">Use system default</SelectItem>
										<SelectItem value="enabled">Enabled for company</SelectItem>
										<SelectItem value="disabled">
											Disabled for company
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Outbound Playbook Calls</Label>
								<Select
									value={formData.outboundPlaybookCalls}
									onValueChange={(value: FeatureOverride) =>
										setFormData({
											...formData,
											outboundPlaybookCalls: value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="inherit">Use system default</SelectItem>
										<SelectItem value="enabled">Enabled for company</SelectItem>
										<SelectItem value="disabled">
											Disabled for company
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>API Access</Label>
								<Select
									value={formData.apiAccess ? "enabled" : "disabled"}
									onValueChange={(value) =>
										setFormData({
											...formData,
											apiAccess: value === "enabled",
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="enabled">Enabled for company</SelectItem>
										<SelectItem value="disabled">
											Disabled for company
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>PII Removal & Redaction</Label>
								<Select
									value={formData.piiRemoval}
									onValueChange={(value: FeatureOverride) =>
										setFormData({
											...formData,
											piiRemoval: value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="inherit">Use system default</SelectItem>
										<SelectItem value="enabled">Enabled for company</SelectItem>
										<SelectItem value="disabled">
											Disabled for company
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Delete Audio from Twilio</Label>
								<Select
									value={formData.deleteAudioFromTwilio}
									onValueChange={(value: FeatureOverride) =>
										setFormData({
											...formData,
											deleteAudioFromTwilio: value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="inherit">Use system default</SelectItem>
										<SelectItem value="enabled">Enabled for company</SelectItem>
										<SelectItem value="disabled">
											Disabled for company
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Delete pii.json from GCP</Label>
								<Select
									value={formData.deletePiiJsonFromGcp}
									onValueChange={(value: FeatureOverride) =>
										setFormData({
											...formData,
											deletePiiJsonFromGcp: value,
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="inherit">Use system default</SelectItem>
										<SelectItem value="enabled">Enabled for company</SelectItem>
										<SelectItem value="disabled">
											Disabled for company
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<DialogFooter>
							<Button variant="outline" onClick={() => setIsDialogOpen(false)}>
								Cancel
							</Button>
							<Button onClick={handleSubmit}>Create Company</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			<TypedDeleteConfirmationDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				entityType="Company"
				entityName={selectedCompany?.name ?? ""}
				consequences="This action cannot be undone and will affect all associated services, users, and agents."
				onConfirm={handleDeleteConfirm}
			/>

			<Card>
				<CardHeader>
					<CardTitle>All Companies</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="mb-4 flex flex-col gap-3 sm:flex-row">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
							<Input
								placeholder="Search by company name, contact, email, phone..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10"
							/>
						</div>
						<Select
							value={customerTypeFilter}
							onValueChange={setCustomerTypeFilter}
						>
							<SelectTrigger
								aria-label="Filter by customer type"
								className="sm:w-52"
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All customer types</SelectItem>
								{Object.entries(CUSTOMER_TYPE_LABELS).map(([key, label]) => (
									<SelectItem key={key} value={key}>
										{label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Company Name</TableHead>
								<TableHead>Customer type</TableHead>
								<TableHead>Contact Person</TableHead>
								<TableHead>Contact Email</TableHead>
								<TableHead>Contact Phone</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredCompanies.map((company) => (
								<TableRow
									key={company.id}
									className="cursor-pointer hover:bg-slate-50"
									onClick={() => onNavigateToCompany(company.id)}
								>
									<TableCell>{company.name}</TableCell>
									<TableCell>
										{
											CUSTOMER_TYPE_LABELS[
												company.customerType ?? "unclassified"
											]
										}
									</TableCell>
									<TableCell>{company.contactName}</TableCell>
									<TableCell>{company.contactEmail}</TableCell>
									<TableCell>{company.contactPhone}</TableCell>
									<TableCell>{formatDate(company.createdAt)}</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													onNavigateToCompany(company.id, true);
												}}
												aria-label={`Edit ${company.name}`}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleDeleteClick(company);
												}}
											>
												<Trash2 className="h-4 w-4 text-red-600" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{filteredCompanies.length === 0 && (
						<div className="text-center py-8 text-slate-500">
							No companies found
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
